import { AppContextService } from '../../../services/app-context.service';
import { ActivatedRoute as ContextRoute } from '@angular/router';
import { Optional as ContextOptional, ElementRef as ContextElement } from '@angular/core';
import { Subscription as ContextSubscription } from 'rxjs';
import { viewContextKey, viewContextVisible } from '../../../shared/view-context';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subscription, switchMap } from 'rxjs';
import { AuthService } from '../../../services/auth.service';

/** One pill on the rail. Key, label and count all come from the server so the
 *  page cannot invent a bucket the bucket law does not have. */
export interface NurtureBucket { key: string; label: string; count: number; }

export interface NurtureCard {
  nurtureId: number;
  leadId: number;
  title: string | null;
  bucket: string;
  wakeOn: string | null;
  wakeTrigger: string;
  wakeLeadDays: number | null;
  contractEnd: string | null;
  incumbentProvider: string | null;
  sleepReasonCode: string;
  sleepReasonNote: string | null;
  wakeReason: string | null;
  wakeReasonSource: string;
  sleptAt: string;
  annualValue: number | null;
  routeFitStatus: string | null;
  salesRepUserId: number | null;
}

export interface NurtureQueue {
  buckets: NurtureBucket[];
  items: NurtureCard[];
  total: number;
  page: number;
  pageSize: number;
  /** The tenant's own today, so "5 days overdue" is counted from the same day
   *  the server bucketed against and not from the browser's clock. */
  today: string;
}

export interface NurtureReasonTally { code: string; count: number; }

export interface NurtureInsights {
  reasons: NurtureReasonTally[];
  totalAsleep: number;
  averageSleepDays: number | null;
  winRateAfterWaking: number | null;
  wokenCount: number;
}

interface QueueQuery { bucket: string | null; salesRep: number | null; search: string; page: number; }

/**
 * Design 3i — Nurture.
 *
 * The organising axis is WHEN IT WAKES. Everything here is stopped in the same
 * place, so grouping by stage or by owner would say nothing; the rail is
 * Overdue / this week / this month / awaiting contract end / no date, and the
 * cards under it are grouped the same way.
 *
 * Every card carries Scout's line on why it is worth waking THIS one now. That
 * line is not decoration: without it the page is a list of things you failed to
 * do. It is why the wake reason has a column of its own rather than living in a
 * note, and why a card with none says so instead of quietly omitting the row.
 *
 * There is no `app-leads-agent-strip` here on purpose. That strip reports
 * automation_job progress, and no job type plans a nurture queue - it would
 * report "idle" on the one page where Scout's presence is visible on every
 * card, which reads as a failure. Scout's surface on 3i is the per-card reason.
 */
@Component({
  selector: 'app-nurture',
  templateUrl: './nurture.component.html',
  styleUrls: ['./nurture.component.css'],
})
export class NurtureComponent implements OnInit, OnDestroy {
  /** The rail order the server sends; the page never hard-codes bucket keys
   *  except to know which section a card belongs under. */
  readonly bucketOrder = ['overdue', 'this-week', 'this-month', 'awaiting-contract-end', 'no-date'];

  readonly reasonLabels: Record<string, string> = {
    WaitingOutContract: 'Waiting out a contract',
    BudgetOrTiming: 'Budget / bad timing',
    NoDecisionMaker: "Can't reach the decision maker",
    RouteFit: 'Route fit too poor',
    Other: 'Other',
  };

  /** The query the list is drawn from. A subject rather than a field because
   *  every control on the page writes to it and one subscription reads it, so
   *  a fast second click cannot leave the older response painted last. */
  readonly query$ = new BehaviorSubject<QueueQuery>({
    bucket: null, salesRep: null, search: '', page: 1,
  });

  queue: NurtureQueue | null = null;
  insights: NurtureInsights | null = null;
  loading = false;
  loadError: string | null = null;
  actionError: string | null = null;
  /** The card whose action is in flight, so its buttons can be disabled
   *  without freezing the rest of the queue. */
  busyLeadId: number | null = null;

  private readonly currentUserId: number | null;
  private sub?: Subscription;

  private viewRegistration?: ContextSubscription;
  private registerViewContext(): void {
    this.viewRegistration = this.viewContext?.registerView({
      key: viewContextKey(this.viewRoute, 'Nurture queue'), name: 'Nurture queue', priority: -10,
      active: () => viewContextVisible(this.viewElement),
      read: () => (this.query$.value),
      describe: state => JSON.stringify(state),
      restore: state => { this.query$.next(state); },
    });
  }

  constructor(private http: HttpClient, auth: AuthService,
    @ContextOptional() private readonly viewContext?: AppContextService,
    @ContextOptional() private readonly viewRoute?: ContextRoute,
    @ContextOptional() private readonly viewElement?: ContextElement<HTMLElement>,
  ) {
    this.currentUserId = auth.tenant.currentUserId;
  }

  ngOnInit(): void {
    this.registerViewContext();
    this.sub = this.query$
      .pipe(switchMap(query => {
        this.loading = true;
        this.loadError = null;
        return this.http.get<NurtureQueue>('/api/nurture', { params: this.toParams(query) });
      }))
      .subscribe({
        next: queue => { this.queue = queue; this.loading = false; },
        error: () => {
          this.loadError = 'Failed to load the nurture queue.';
          this.queue = null;
          this.loading = false;
        },
      });
    this.loadInsights();
  }

  ngOnDestroy(): void {
    this.viewRegistration?.unsubscribe();
    this.sub?.unsubscribe();
    this.query$.complete();
  }

  private toParams(query: QueueQuery): Record<string, string> {
    const params: Record<string, string> = { page: String(query.page), pageSize: '25' };
    if (query.bucket) params['bucket'] = query.bucket;
    if (query.salesRep != null) params['salesRep'] = String(query.salesRep);
    if (query.search.trim()) params['search'] = query.search.trim();
    return params;
  }

  private loadInsights(): void {
    this.http.get<NurtureInsights>('/api/nurture/insights').subscribe({
      // Best-effort: the right rail is a nudge. Losing it must not cost the
      // queue, which is the thing people came for.
      next: insights => { this.insights = insights; },
      error: () => { this.insights = null; },
    });
  }

  reload(): void {
    this.query$.next({ ...this.query$.value });
    this.loadInsights();
  }

  get query(): QueueQuery { return this.query$.value; }

  selectBucket(key: string | null): void {
    this.query$.next({ ...this.query, bucket: key, page: 1 });
  }

  toggleMine(): void {
    const salesRep = this.query.salesRep == null ? this.currentUserId : null;
    this.query$.next({ ...this.query, salesRep, page: 1 });
  }

  get mineOnly(): boolean { return this.query.salesRep != null; }
  get canFilterMine(): boolean { return this.currentUserId != null; }

  setSearch(value: string): void {
    this.query$.next({ ...this.query, search: value, page: 1 });
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.pageCount) return;
    this.query$.next({ ...this.query, page });
  }

  get pageCount(): number {
    if (!this.queue || this.queue.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(this.queue.total / this.queue.pageSize));
  }

  /** The whole queue, for the rail's leading pill. */
  get totalAsleep(): number {
    return this.queue?.buckets.reduce((sum, bucket) => sum + bucket.count, 0) ?? 0;
  }

  bucketCount(key: string): number {
    return this.queue?.buckets.find(bucket => bucket.key === key)?.count ?? 0;
  }

  bucketLabel(key: string): string {
    return this.queue?.buckets.find(bucket => bucket.key === key)?.label ?? key;
  }

  /** Sections in rail order, empty ones dropped. A page is grouped by when its
   *  cards wake even when the rail is showing everything. */
  get sections(): { key: string; label: string; cards: NurtureCard[] }[] {
    const items = this.queue?.items ?? [];
    return this.bucketOrder
      .map(key => ({
        key,
        label: this.bucketLabel(key),
        cards: items.filter(card => card.bucket === key),
      }))
      .filter(section => section.cards.length > 0);
  }

  // ── What a card says about when it wakes ───────────────────────────────

  /** Days between the tenant's today and a card's wake date. Positive = late. */
  daysOverdue(card: NurtureCard): number | null {
    if (!card.wakeOn || !this.queue) return null;
    const days = this.dayDiff(this.queue.today, card.wakeOn);
    return days > 0 ? days : null;
  }

  /** The line under the title: the one fact the card is organised by. */
  wakeLine(card: NurtureCard): string {
    const late = this.daysOverdue(card);
    if (late != null) return `${late} day${late === 1 ? '' : 's'} overdue`;
    if (card.wakeTrigger === 'ContractEnd' && !card.wakeOn) return 'awaiting contract end';
    if (!card.wakeOn) return 'no date set';
    if (this.queue && card.wakeOn === this.queue.today) return 'wakes today';
    return `wakes ${this.formatDate(card.wakeOn)}`;
  }

  /** How long it has been asleep — the design's "asleep 47d" chip. */
  asleepDays(card: NurtureCard): number {
    const slept = Date.parse(card.sleptAt);
    if (Number.isNaN(slept)) return 0;
    return Math.max(0, Math.round((Date.now() - slept) / 86_400_000));
  }

  reasonLabel(code: string): string { return this.reasonLabels[code] ?? code; }

  /**
   * How many sleeping pursuits are filed under "route fit too poor". The design
   * asks the screen to point at this pile rather than let it rot: a pursuit
   * that will never be worth the drive is Disqualified, and parking it in the
   * sleep queue only means it comes back to be dismissed again.
   */
  get misfiledRouteFit(): number {
    return this.insights?.reasons.find(reason => reason.code === 'RouteFit')?.count ?? 0;
  }

  /** The share of a reason's bar in the right rail. */
  reasonShare(tally: NurtureReasonTally): number {
    const total = this.insights?.totalAsleep ?? 0;
    return total > 0 ? Math.round((tally.count / total) * 100) : 0;
  }

  // ── Actions ───────────────────────────────────────────────────────────

  /** Wake it back into the pipeline, at the stage it slept from. */
  followUp(card: NurtureCard): void {
    this.act(card, `/api/nurture/${card.leadId}/wake`, { outcome: 'Resumed' });
  }

  /** Snooze: the same wait, a month further out. The design's per-card Snooze;
   *  a picked date is a later control, and guessing one silently would be
   *  worse than a stated default. */
  snooze(card: NurtureCard): void {
    if (!this.queue) return;
    const from = card.wakeOn && card.wakeOn > this.queue.today ? card.wakeOn : this.queue.today;
    this.act(card, `/api/nurture/${card.leadId}/reschedule`, {
      wakeTrigger: 'SetDate',
      wakeOn: this.addDays(from, 30),
    });
  }

  private act(card: NurtureCard, url: string, body: unknown): void {
    this.actionError = null;
    this.busyLeadId = card.leadId;
    this.http.post(url, body).subscribe({
      next: () => { this.busyLeadId = null; this.reload(); },
      error: err => {
        this.actionError = err?.error?.error ?? 'That did not go through.';
        this.busyLeadId = null;
      },
    });
  }

  // ── Dates, as plain yyyy-MM-dd strings ────────────────────────────────
  //
  // The server sends a DateOnly and the tenant's own today. Parsing either into
  // a Date would drag the browser's zone into a comparison that is meant to be
  // the tenant's, so the arithmetic here stays on the string form.

  private dayDiff(from: string, to: string): number {
    return Math.round((Date.parse(`${from}T00:00:00Z`) - Date.parse(`${to}T00:00:00Z`)) / 86_400_000);
  }

  private addDays(date: string, days: number): string {
    const shifted = new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000);
    return shifted.toISOString().slice(0, 10);
  }

  formatDate(date: string): string {
    const parsed = new Date(Date.parse(`${date}T00:00:00Z`));
    return parsed.toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', timeZone: 'UTC',
    });
  }
}
